import React, { useMemo, useState } from "react";
import TowerDayNotice from "./TowerDayNotice";
import Card from "../ui/Card";
import Input from "../ui/Input";
import TowerShipmentList from "./TowerShipmentList";
import { flexInTransitList, searchListed } from "./model/locateDrivers";
import { useTowerDay } from "./hooks/useTowerDay";
import { formatCents } from "./model/towerFormat";
import TowerZoneCharts, { ZoneAlertCounts } from "./TowerZoneCharts";
import { driverAlertRows, isToday as isTodayShipment } from "./model/monitorAlerts";
import { isListedAlert } from "./model/geoAlertsView";
import type { TowerDashboard } from "../../types/controlTower";

// Resumen de la Torre: por defecto lo colectado en el día (fecha de colecta);
// "Operación del día" muestra lo que tuvieron los choferes. Con selector de día.
export default function TowerSummary({
  data,
  today,
  token,
  countLabel,
  operationalDateLabel,
  onGoTo,
  nowMs,
}: {
  data: TowerDashboard;
  today: string;
  token: string | null;
  countLabel: string;
  operationalDateLabel: string;
  // Atajos de las tortas por zona a Monitoreo o a Alertas geográficas.
  onGoTo: (view: "drivers" | "geo") => void;
  // Reloj de la página: la inactividad se recalcula aunque no lleguen datos nuevos.
  nowMs: number;
}) {
  const [summaryBase, setSummaryBase] = useState<"collected" | "operation">("collected");
  const { day, setDay, data: source, error, retry, pastNote } = useTowerDay(today, data, token);
  const isToday = day === today;
  const usingCollected = summaryBase === "collected" && Boolean(source?.collectedSummary);
  const resumen = usingCollected ? source?.collectedSummary : source?.summary;
  // Panel desplegable de "En reparto Flex": los envíos, con búsqueda.
  const [flexOpen, setFlexOpen] = useState(false);
  const [flexQuery, setFlexQuery] = useState("");
  const flexList = useMemo(
    () => flexInTransitList(source?.drivers || [], source?.depot?.shipments || [], usingCollected ? day : undefined),
    [source, usingCollected, day],
  );
  const flexShown = searchListed(flexList, flexQuery);
  // Tortas por zona: los envíos de choferes del mismo corte que las tarjetas.
  const zoneShipments = useMemo(
    () =>
      (source?.drivers || []).flatMap((d) =>
        d.shipments.filter((s) => !usingCollected || s.operationalDate?.startsWith(day)),
      ),
    [source, usingCollected, day],
  );
  // Atajo a las alertas de cada zona (solo hoy: la inactividad se mide en vivo).
  const zoneAlerts = useMemo(() => {
    if (!source || !isToday) return null;
    const result: Record<string, ZoneAlertCounts> = {};
    const add = (zone: string, key: keyof ZoneAlertCounts) => {
      result[zone] = result[zone] || { inactivity: 0, geo: 0 };
      result[zone][key]++;
    };
    for (const zone of ["NORTE", "SUR", "CABA", "OESTE"]) result[zone] = { inactivity: 0, geo: 0 };
    // Cada alerta cuenta en las zonas donde el chofer tiene Flex en camino de hoy,
    // igual que en Monitoreo.
    for (const row of driverAlertRows(source.drivers, nowMs)) {
      const zones = new Set(
        row.driver.shipments
          .filter((s) => s.status === "inTransit" && s.shippingUniverse === "FLEX" && isTodayShipment(s))
          .map((s) => s.zone),
      );
      for (const zone of zones) add(zone, "inactivity");
    }
    for (const alert of (source.geoAlerts?.items || []).filter(isListedAlert)) add(alert.shipment.zone, "geo");
    return result;
  }, [source, isToday, nowMs]);
  const dayInput = (
    <label className="tower-inline-date">
      Día
      <input type="date" className="tower-date" value={day} max={today} onChange={(e) => setDay(e.target.value)} />
    </label>
  );
  return (
    <>
      <div className="tower-summary-controls">
        <div className="tower-map-filter-group" role="group" aria-label="Base del resumen">
          <button
            type="button"
            className={`tower-map-filter${summaryBase === "collected" ? " active" : ""}`}
            aria-pressed={summaryBase === "collected"}
            onClick={() => setSummaryBase("collected")}
            title="Solo lo colectado ese día."
          >
            Fecha de colecta
          </button>
          <button
            type="button"
            className={`tower-map-filter${summaryBase === "operation" ? " active" : ""}`}
            aria-pressed={summaryBase === "operation"}
            onClick={() => setSummaryBase("operation")}
            title="Lo que tuvieron los choferes ese día: colectados, movidos ese día y, hoy, lo que sigue en curso de días anteriores."
          >
            Operación del día
          </button>
        </div>
        {dayInput}
      </div>
      <TowerDayNotice error={error} onRetry={retry} pastNote={pastNote} />
      {!source && !error && (
        <p className="tower-muted" role="status">
          Cargando…
        </p>
      )}
      {source && resumen && (
        <>
          <section className="tower-kpis tower-summary-kpis" aria-label="Resumen del día">
            <Card className="tower-summary-kpi" padding="16px">
              <small>{usingCollected ? (isToday ? "Colectados hoy" : "Colectados") : `${countLabel} del día`}</small>
              <strong>{resumen.total.toLocaleString("es-AR")}</strong>
              {!usingCollected && resumen.previousDays > 0 && (
                <span className="tower-kpi-note">
                  {resumen.previousDays.toLocaleString("es-AR")} de días anteriores
                </span>
              )}
            </Card>
            <Card className="tower-summary-kpi" padding="16px">
              <small>Envíos Flex</small>
              <strong>{resumen.flexTotal.toLocaleString("es-AR")}</strong>
              {!usingCollected && resumen.flexPreviousDays > 0 && (
                <span className="tower-kpi-note">
                  {resumen.flexPreviousDays.toLocaleString("es-AR")} de días anteriores
                </span>
              )}
            </Card>
            <Card className="tower-summary-kpi" padding="16px">
              <small>Envíos particulares</small>
              <strong>
                {Math.max(0, resumen.total - resumen.flexTotal - resumen.unknownShipping).toLocaleString("es-AR")}
              </strong>
              {!usingCollected && resumen.nonFlexPreviousDays > 0 && (
                <span className="tower-kpi-note">
                  {resumen.nonFlexPreviousDays.toLocaleString("es-AR")} de días anteriores
                </span>
              )}
            </Card>
            <Card className="tower-summary-kpi tower-summary-warning" padding="16px">
              <small>Sin asignar</small>
              <strong>{resumen.unassigned.toLocaleString("es-AR")}</strong>
            </Card>
            <Card className="tower-summary-kpi tower-summary-gold" padding="16px">
              <small>En reparto total</small>
              <strong>{resumen.inTransit.toLocaleString("es-AR")}</strong>
            </Card>
            <button
              type="button"
              className="tower-kpi-toggle"
              aria-expanded={flexOpen}
              aria-controls="tower-flex-panel"
              onClick={() => setFlexOpen((open) => !open)}
            >
              <Card className="tower-summary-kpi tower-summary-highlight" padding="16px">
                <small>En reparto Flex</small>
                <strong>{resumen.flexInTransit.toLocaleString("es-AR")}</strong>
                <span className="tower-kpi-note">{flexOpen ? "Ocultar envíos ▴" : "Ver envíos ▾"}</span>
              </Card>
            </button>
            <Card className="tower-summary-kpi tower-summary-gold" padding="16px">
              <small>En reparto particulares</small>
              <strong>{resumen.nonFlexInTransit.toLocaleString("es-AR")}</strong>
            </Card>
            <Card className="tower-summary-kpi" padding="16px">
              <small>Choferes activos</small>
              <strong>{resumen.activeDrivers.toLocaleString("es-AR")}</strong>
            </Card>
          </section>
          <Card className="tower-summary-progress" padding="18px">
            <div className="tower-summary-progress-head">
              <div>
                <small>Estado operativo</small>
                <strong>
                  {resumen.total ? ((resumen.delivered / resumen.total) * 100).toFixed(1) : "0"}% completado
                </strong>
              </div>
              <span>
                Actualizado{" "}
                {new Date(source.generatedAt).toLocaleTimeString("es-AR", {
                  timeZone: "America/Argentina/Buenos_Aires",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <progress
              max={100}
              value={resumen.total ? (resumen.delivered / resumen.total) * 100 : 0}
              aria-label={`${resumen.total ? ((resumen.delivered / resumen.total) * 100).toFixed(1) : "0"}% de envíos entregados`}
            />
            <div className="tower-summary-progress-stats">
              <span>
                <b>{resumen.delivered.toLocaleString("es-AR")}</b> entregados
              </span>
              <span>
                <b>{resumen.inTransit.toLocaleString("es-AR")}</b> en reparto
              </span>
              <span>
                <b>{resumen.unassigned.toLocaleString("es-AR")}</b> sin asignar
              </span>
              <span>
                <b>{resumen.incident.toLocaleString("es-AR")}</b> con incidencia
              </span>
              <span>
                <b>{resumen.delayed.toLocaleString("es-AR")}</b> en camino (por Meli)
              </span>
              <span>
                <b>{resumen.rescheduled.toLocaleString("es-AR")}</b> reprogramados por comprador
              </span>
              {resumen.collections > 0 && (
                <span title={`Cobrado: ${formatCents(resumen.collectedCents)}`}>
                  <b>{resumen.collections.toLocaleString("es-AR")}</b> cobros · {formatCents(resumen.collectionCents)}
                </span>
              )}
              {(resumen.exchanges > 0 || resumen.pickups > 0) && (
                <span>
                  <b>{resumen.exchanges.toLocaleString("es-AR")}</b> cambios ·{" "}
                  <b>{resumen.pickups.toLocaleString("es-AR")}</b> retiros
                </span>
              )}
            </div>
          </Card>
          <TowerZoneCharts
            shipments={zoneShipments}
            alerts={zoneAlerts}
            onOpenMonitor={() => onGoTo("drivers")}
            onOpenGeo={() => onGoTo("geo")}
          />
          {flexOpen && (
            <Card className="tower-flex-panel" padding="16px">
              <div id="tower-flex-panel">
                <div className="tower-toolbar">
                  <div className="tower-search">
                    <Input
                      label="Buscar"
                      type="search"
                      placeholder="Vendedor, dirección o chofer"
                      value={flexQuery}
                      onChange={(e) => setFlexQuery(e.target.value)}
                    />
                  </div>
                  <span className="tower-muted">
                    {flexShown.length} de {flexList.length} envíos
                  </span>
                </div>
                <TowerShipmentList
                  shipments={flexShown}
                  showZone
                  operationalDateLabel={operationalDateLabel}
                  emptyText={flexList.length ? "No hay envíos que coincidan." : "No hay envíos Flex en camino."}
                />
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
}
